import { Link } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import ProfitSystemLogo from "../components/ProfitSystemLogo";

const TermsConditions = () => {
  const accountObligations = [
    "Proporcionar información veraz",
    "Mantener la confidencialidad de sus credenciales",
    "No utilizar el servicio para fines ilícitos"
  ];
  const integrationItems = [
    "No almacena contraseñas",
    "No modifica campañas",
    "No ejecuta acciones en cuentas publicitarias"
  ];
  const liabilityNoGuarantee = [
    "Exactitud absoluta de datos provenientes de terceros",
    "Disponibilidad ininterrumpida",
    "Compatibilidad permanente con APIs externas"
  ];
  const liabilityNotResponsible = [
    "Decisiones empresariales tomadas en base a los datos",
    "Cambios en APIs externas",
    "Suspensiones de cuentas en plataformas de terceros"
  ];
  const intellectualPropertyItems = [
    "Copiar",
    "Reproducir",
    "Revender",
    "Descompilar sin autorización expresa."
  ];
  const cancellationItems = [
    "Uso fraudulento",
    "Incumplimiento contractual",
    "Riesgo para la seguridad del sistema"
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <nav className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-nav-home-link">
            <ProfitSystemLogo height={40} />
          </Link>
          <Link to="/" className="text-sm font-medium text-slate-600 hover:text-red-600" data-testid="terms-nav-back-link">
            Volver al inicio
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <header className="mb-12" data-testid="terms-header">
          <p className="text-sm font-semibold text-red-600 uppercase tracking-wide" data-testid="terms-kicker">
            Legal
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-3" data-testid="terms-title">
            Términos y condiciones
          </h1>
          <p className="text-slate-600 mt-4" data-testid="terms-subtitle">
            Última actualización: Febrero 2025
          </p>
        </header>

        <section className="space-y-4 mb-10" data-testid="terms-section-1">
          <h2 className="text-xl font-bold text-slate-900" data-testid="terms-section-1-title">1. Objeto</h2>
          <p className="text-slate-600" data-testid="terms-section-1-text">
            El presente documento regula el acceso y uso del software SaaS ofrecido por Eleven Group EDT SLU, destinado al análisis de datos publicitarios y métricas financieras.
          </p>
        </section>

        <section className="space-y-4 mb-10" data-testid="terms-section-2">
          <h2 className="text-xl font-bold text-slate-900" data-testid="terms-section-2-title">2. Naturaleza del servicio</h2>
          <p className="text-slate-600" data-testid="terms-section-2-text">
            La Empresa proporciona una herramienta de análisis de datos basada en la información autorizada por el usuario. No somos afiliados ni representamos oficialmente a Google, Meta u otras plataformas conectadas.
          </p>
        </section>

        <section className="space-y-4 mb-10" data-testid="terms-section-3">
          <h2 className="text-xl font-bold text-slate-900" data-testid="terms-section-3-title">3. Registro y cuentas</h2>
          <p className="text-slate-600" data-testid="terms-section-3-intro">El usuario se compromete a:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {accountObligations.map((item, index) => (
              <li key={item} data-testid={`terms-section-3-item-${index}`}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="space-y-4 mb-10" data-testid="terms-section-4">
          <h2 className="text-xl font-bold text-slate-900" data-testid="terms-section-4-title">4. Integraciones externas</h2>
          <p className="text-slate-600" data-testid="terms-section-4-intro">
            El usuario autoriza expresamente el acceso a datos mediante sistemas OAuth.
          </p>
          <p className="text-slate-600" data-testid="terms-section-4-lead">La Empresa:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {integrationItems.map((item, index) => (
              <li key={item} data-testid={`terms-section-4-item-${index}`}>{item}</li>
            ))}
          </ul>
          <p className="text-slate-600" data-testid="terms-section-4-note">
            El acceso se limita a lectura de datos para análisis.
          </p>
        </section>

        <section className="space-y-4 mb-10" data-testid="terms-section-5">
          <h2 className="text-xl font-bold text-slate-900" data-testid="terms-section-5-title">5. Limitación de responsabilidad</h2>
          <p className="text-slate-600" data-testid="terms-section-5-intro">
            El servicio se ofrece “tal cual”.
          </p>
          <p className="text-slate-600" data-testid="terms-section-5-subintro">No garantizamos:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {liabilityNoGuarantee.map((item, index) => (
              <li key={item} data-testid={`terms-section-5-noguarantee-item-${index}`}>{item}</li>
            ))}
          </ul>
          <p className="text-slate-600" data-testid="terms-section-5-responsibility-intro">No somos responsables de:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {liabilityNotResponsible.map((item, index) => (
              <li key={item} data-testid={`terms-section-5-notresponsible-item-${index}`}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="space-y-4 mb-10" data-testid="terms-section-6">
          <h2 className="text-xl font-bold text-slate-900" data-testid="terms-section-6-title">6. Propiedad intelectual</h2>
          <p className="text-slate-600" data-testid="terms-section-6-intro">
            Todo el software, diseño y código pertenecen a la Empresa. El usuario no puede:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {intellectualPropertyItems.map((item, index) => (
              <li key={item} data-testid={`terms-section-6-item-${index}`}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="space-y-4 mb-10" data-testid="terms-section-7">
          <h2 className="text-xl font-bold text-slate-900" data-testid="terms-section-7-title">7. Precio y facturación</h2>
          <p className="text-slate-600" data-testid="terms-section-7-text">
            El uso del servicio puede estar sujeto a suscripción. El impago podrá suponer la suspensión del acceso.
          </p>
        </section>

        <section className="space-y-4 mb-10" data-testid="terms-section-8">
          <h2 className="text-xl font-bold text-slate-900" data-testid="terms-section-8-title">8. Cancelación</h2>
          <p className="text-slate-600" data-testid="terms-section-8-intro">El usuario puede cancelar en cualquier momento.</p>
          <p className="text-slate-600" data-testid="terms-section-8-lead">La Empresa puede suspender cuentas en caso de:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {cancellationItems.map((item, index) => (
              <li key={item} data-testid={`terms-section-8-item-${index}`}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="space-y-4" data-testid="terms-section-9">
          <h2 className="text-xl font-bold text-slate-900" data-testid="terms-section-9-title">9. Legislación aplicable</h2>
          <p className="text-slate-600" data-testid="terms-section-9-text">
            El presente acuerdo se rige por la legislación española. Las partes se someten a los tribunales del domicilio social de la Empresa.
          </p>
        </section>
      </main>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500" data-testid="terms-footer-text">
            © 2025 ProfitSystem. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/terminos-y-condiciones" className="text-slate-500 hover:text-red-600" data-testid="terms-footer-terms-link">
              Términos y condiciones
            </Link>
            <Link to="/politica-de-privacidad" className="text-slate-500 hover:text-red-600" data-testid="terms-footer-privacy-link">
              Política de privacidad
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditions;
